/**
 * Символы раздела «Электрика» для плана — по компоненту на элемент каталога.
 *
 * Рисунок задан в собственном viewBox символа и растягивается в габарит объекта
 * (UI.SymbolFrame). Цвет — через currentColor, поэтому символ перекрашивается в цвет
 * типа или выделения. Каталог хранит только данные; как элемент выглядит, знает
 * этот файл. Ключ реестра — id элемента каталога.
 */
import { ElectricalId } from "@/shared/catalog";
import { UIKit, type SymbolComponent } from "@/shared/ui";

/** Розетка */
export const SocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="18"
			y="18"
			width="44"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<circle cx="40" cy="40" r="6" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Двойная розетка */
export const DoubleSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 152 80" {...props}>
		<rect
			x="12"
			y="18"
			width="56"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<rect
			x="84"
			y="18"
			width="56"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<circle cx="40" cy="40" r="6" fill="currentColor" />
		<circle cx="112" cy="40" r="6" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Силовая розетка */
export const PowerSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<rect
			x="20"
			y="20"
			width="60"
			height="60"
			rx="12"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path d="M35 50h30M50 35v30" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** ТВ-розетка */
export const TvSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="18"
			y="18"
			width="44"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			d="M26 48L40 32l14 16M40 32v22"
			stroke="currentColor"
			strokeWidth="4"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</UIKit.SymbolFrame>
);

/** Интернет-розетка */
export const NetSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="18"
			y="18"
			width="44"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			d="M22 42h36M27 32h26M30 54h20"
			stroke="currentColor"
			strokeWidth="4"
			strokeLinecap="round"
		/>
	</UIKit.SymbolFrame>
);

/** Выключатель */
export const SwitchSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="18"
			y="18"
			width="44"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			d="M30 50l10-14 10 14"
			stroke="currentColor"
			strokeWidth="4"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</UIKit.SymbolFrame>
);

/** Двойной выключатель */
export const DoubleSwitchSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 152 80" {...props}>
		<rect
			x="12"
			y="18"
			width="56"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<rect
			x="84"
			y="18"
			width="56"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			d="M28 48l10-14 10 14M100 48l10-14 10 14"
			stroke="currentColor"
			strokeWidth="4"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</UIKit.SymbolFrame>
);

/** Распределительная коробка */
export const JunctionBoxSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 120 120" {...props}>
		<rect
			x="10"
			y="10"
			width="100"
			height="100"
			rx="10"
			fill="none"
			stroke="currentColor"
			strokeWidth="5"
		/>
		<path d="M10 40h100M60 40v70" stroke="currentColor" strokeWidth="4" />
		<circle cx="36" cy="75" r="7" fill="currentColor" />
		<circle cx="84" cy="75" r="7" fill="currentColor" />
		<text
			x="60"
			y="31"
			textAnchor="middle"
			fontSize="18"
			fontFamily="sans-serif"
			fontWeight="700"
			fill="currentColor"
		>
			РК
		</text>
	</UIKit.SymbolFrame>
);

/** Вывод под кондиционер: рамка вывода и снежинка */
export const AcSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="14"
			y="14"
			width="52"
			height="52"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			d="M40 24v32M26 32l28 16M26 48l28-16M35 25l5 5 5-5M35 55l5-5 5 5"
			stroke="currentColor"
			strokeWidth="3.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</UIKit.SymbolFrame>
);

/** Электрощит: корпус, рейка с автоматами и подпись «ЩК» */
export const PanelSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 120 160" {...props}>
		<rect
			x="8"
			y="8"
			width="104"
			height="144"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="6"
		/>
		<path d="M8 62h104" stroke="currentColor" strokeWidth="4" />
		{[24, 44, 64, 84].map((x) => (
			<rect key={x} x={x} y="74" width="12" height="26" rx="2" fill="currentColor" />
		))}
		<text
			x="60"
			y="46"
			textAnchor="middle"
			fontSize="30"
			fontFamily="sans-serif"
			fontWeight="700"
			fill="currentColor"
		>
			ЩК
		</text>
	</UIKit.SymbolFrame>
);

const socketBlockSymbol =
	(count: number): SymbolComponent =>
	(props) => (
		<UIKit.SymbolFrame viewBox={`0 0 ${count * 72 + 8} 80`} {...props}>
			{Array.from({ length: count }, (_, index) => (
				<g key={index} transform={`translate(${index * 72}, 0)`}>
					<rect
						x="12"
						y="18"
						width="56"
						height="44"
						rx="8"
						fill="none"
						stroke="currentColor"
						strokeWidth="4"
					/>
					<circle cx="40" cy="40" r="6" fill="currentColor" />
				</g>
			))}
		</UIKit.SymbolFrame>
	);

const UsbSocketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 80 80" {...props}>
		<rect
			x="18"
			y="18"
			width="44"
			height="44"
			rx="8"
			fill="none"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<rect x="27" y="32" width="26" height="8" rx="2" fill="currentColor" />
		<rect x="30" y="46" width="20" height="6" rx="3" fill="currentColor" />
	</UIKit.SymbolFrame>
);

const TripleSwitchSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 224 80" {...props}>
		{[0, 72, 144].map((offset) => (
			<g key={offset} transform={`translate(${offset}, 0)`}>
				<rect
					x="12"
					y="18"
					width="56"
					height="44"
					rx="8"
					fill="none"
					stroke="currentColor"
					strokeWidth="4"
				/>
				<path
					d="M28 48l10-14 10 14"
					stroke="currentColor"
					strokeWidth="4"
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>
			</g>
		))}
	</UIKit.SymbolFrame>
);

export const ELECTRICAL_SYMBOLS: Record<ElectricalId, SymbolComponent> = {
	[ElectricalId.Socket]: SocketSymbol,
	[ElectricalId.SocketDouble]: DoubleSocketSymbol,
	[ElectricalId.SocketTriple]: socketBlockSymbol(3),
	[ElectricalId.SocketQuadruple]: socketBlockSymbol(4),
	[ElectricalId.SocketQuintuple]: socketBlockSymbol(5),
	[ElectricalId.SocketUsb]: UsbSocketSymbol,
	[ElectricalId.SocketPower]: PowerSocketSymbol,
	[ElectricalId.SocketThreePhase]: PowerSocketSymbol,
	[ElectricalId.SocketTv]: TvSocketSymbol,
	[ElectricalId.SocketNet]: NetSocketSymbol,
	[ElectricalId.Switch]: SwitchSymbol,
	[ElectricalId.SwitchDouble]: DoubleSwitchSymbol,
	[ElectricalId.SwitchTriple]: TripleSwitchSymbol,
	[ElectricalId.JunctionBox]: JunctionBoxSymbol,
	[ElectricalId.CeilingJunctionBox]: JunctionBoxSymbol,
	[ElectricalId.SocketAc]: AcSocketSymbol,
	[ElectricalId.Panel]: PanelSymbol,
	[ElectricalId.LowVoltagePanel]: NetSocketSymbol,
};
